/**
 * RestaurantSavedFiles — surfaces every snapshot (schedule, inventory,
 * checklist, etc.) authored while inside this restaurant. Mirrors how
 * the department workspace exposes its scoped saved files.
 *
 * A snapshot is considered "owned by this restaurant" when
 *   snapshot.owner_node_id === `restaurant.{restaurantId}`
 *
 * Clicking a row opens the universal SnapshotViewerDialog which renders
 * the branded header + the kind-specific view + a 1-click Print button.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { FolderOpen, FileSpreadsheet, FileText as FileIcon, CalendarDays, Loader2, Archive, Trash2 } from 'lucide-react';
import SnapshotViewerDialog from './SnapshotViewerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  inventory: <FileSpreadsheet className="w-4 h-4" style={{ color: '#15803D' }} />,
  schedule: <CalendarDays className="w-4 h-4" style={{ color: '#003DA5' }} />,
  checklist: <FileIcon className="w-4 h-4" style={{ color: '#7C3AED' }} />,
  permit: <FileIcon className="w-4 h-4" style={{ color: '#B45309' }} />,
};

function fmtRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function RestaurantSavedFiles({ outlet, accentColor }) {
  const ownerNodeId = outlet?.id ? `restaurant.${outlet.id}` : null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const accent = accentColor || '#003DA5';

  const refresh = async () => {
    if (!ownerNodeId) return;
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/snapshots`, {
        params: { owner_node_id: ownerNodeId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ownerNodeId]);

  // Refresh on every save event from inside this restaurant (schedule save,
  // inventory save, etc.) — listeners stay cheap, no polling.
  useEffect(() => {
    const onSaved = () => refresh();
    window.addEventListener('snapshot-saved', onSaved);
    return () => window.removeEventListener('snapshot-saved', onSaved);
  }, [ownerNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ownerNodeId) return null;

  return (
    <section
      className="rounded-2xl p-4 sm:p-5"
      data-testid="restaurant-saved-files"
      style={{
        background: 'linear-gradient(180deg, rgba(56,189,248,0.05) 0%, rgba(255,255,255,0.95) 80%)',
        border: `1px solid ${accent}25`,
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: `${accent}15`, color: accent }}>
            <FolderOpen className="w-4 h-4" />
          </span>
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: accent, fontFamily: 'Manrope' }}>
              Saved Files
            </p>
            <p className="text-sm font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
              {outlet?.name ? `${outlet.name} · ` : ''}schedules, lists & docs
            </p>
          </div>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} />}
      </div>

      {rows.length === 0 && !loading && (
        <p className="text-xs italic px-2 py-3" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          No saved files for this restaurant yet. Save a schedule or list — it will appear here automatically.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="space-y-1.5" data-testid="restaurant-saved-files-list">
          {rows.map((s) => (
            <li key={s.id} className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSelected(s)}
                className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all hover:-translate-y-0.5"
                style={{ background: 'rgba(255,255,255,0.85)', border: `1px solid ${accent}20` }}
                data-testid={`saved-file-${s.id}`}
              >
                <span className="flex-shrink-0">{ICONS[s.kind] || <FileIcon className="w-4 h-4 text-gray-400" />}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                    {s.title}
                  </span>
                  <span className="block text-[10px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                    {s.kind} · saved {fmtRelative(s.created_at)}
                  </span>
                </span>
              </button>
              {/* Archive — soft-hide, restore later from Admin → Archive. */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    await axios.put(
                      `${API}/admin/archive/snapshot/${s.id}`,
                      {},
                      { headers: { Authorization: `Bearer ${typeof window !== 'undefined' ? localStorage.getItem('admin_token') : ''}` } },
                    );
                    setRows((rs) => rs.filter((r) => r.id !== s.id));
                  } catch (e) { alert(e?.response?.data?.detail || 'Archive failed'); }
                }}
                className="p-1.5 rounded-md hover:bg-amber-100 text-amber-700 flex-shrink-0"
                data-testid={`saved-file-archive-${s.id}`}
                title="Archive — restorable later from Admin → Archive"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              {/* Permanent delete — confirmation guard included. */}
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Permanently delete "${s.title}"? Pick Archive instead if you might want it back.`)) return;
                  try {
                    await axios.delete(
                      `${API}/admin/snapshots/${s.id}`,
                      { headers: { Authorization: `Bearer ${typeof window !== 'undefined' ? localStorage.getItem('admin_token') : ''}` } },
                    );
                    setRows((rs) => rs.filter((r) => r.id !== s.id));
                  } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
                }}
                className="p-1.5 rounded-md hover:bg-red-100 text-red-600 flex-shrink-0"
                data-testid={`saved-file-delete-${s.id}`}
                title="Permanently delete saved file"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <SnapshotViewerDialog
        snapshot={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </section>
  );
}
